// lib/services/capture/camera_options.ts
//
// Pure, dependency-free helpers that turn the raw enumerated camera list into
// the user-facing options shown in the camera selector: a deterministic,
// friendly per-facing numbering ("back 1", "front 2", "camera 1") plus the real
// device label/settings so two cameras of the same facing stay distinguishable.
//
// No React, no CaptureService, no browser APIs — safe to unit-test directly.

// Mirror of CameraInfo["facing"] (kept local so this module has zero runtime
// imports and stays trivially testable in the CommonJS unit build). The literal
// unions are identical, so CameraInfo[] is structurally assignable here.
export type CameraFacing = 'front' | 'back' | 'unknown';

export interface CameraDescriptor {
  device_id: string;
  label: string;
  facing: CameraFacing;
  width: number;
  height: number;
  framerate: number;
}

export interface CameraOption {
  device_id: string;
  // Deterministic friendly name, numbered within the facing group.
  friendly: string;
  facing: CameraFacing;
  // Real device label from the browser (may be empty pre-permission).
  label: string;
  // Compact real settings string so same-facing cameras remain distinguishable.
  detail: string;
  // Short, quick-comparison suffix of the deviceId (e.g. "…A1B2"); see
  // format_device_id_suffix. The FULL deviceId is retained in device_id and in
  // detailed dumps/logs — this is only an at-a-glance aid.
  id_suffix: string;
}

const FACING_PREFIX: Record<CameraFacing, string> = {
  back: 'back',
  front: 'front',
  unknown: 'camera',
};

// Format the final characters of a device identifier as a compact, quick
// comparison aid. NEVER mutates or lowercases the identifier — the exact
// characters are preserved.
//   - non-empty, more than 4 user-visible chars -> "…ABCD" (last 4)
//   - 1..4 chars                                -> the whole id, no ellipsis
//   - empty / not a string                      -> "unknown id"
// Length and slicing are done over Unicode code points (Array.from), so a
// trailing emoji or astral character counts as one visible character rather
// than splitting a surrogate pair.
export function format_device_id_suffix(
  device_id: string | null | undefined
): string {
  if (typeof device_id !== 'string' || device_id.length === 0) {
    return 'unknown id';
  }
  const chars = Array.from(device_id);
  if (chars.length <= 4) return device_id;
  return `…${chars.slice(-4).join('')}`;
}

// Compose a friendly name with its deviceId suffix for a single-line label used
// by the selector UI and the scan-start diagnostic, e.g. "back 3 · …A1B2".
export function friendly_with_suffix(
  friendly: string,
  device_id: string | null | undefined
): string {
  return `${friendly} · ${format_device_id_suffix(device_id)}`;
}

function describe_settings(c: CameraDescriptor): string {
  const dims = c.width && c.height ? `${c.width}x${c.height}` : 'size unknown';
  const fps = c.framerate ? `@${Math.round(c.framerate)}fps` : '';
  return `${dims}${fps}`;
}

// Build the ordered option list. Numbering is deterministic: within each facing
// group, cameras are numbered 1..N in the ORIGINAL enumeration order (the order
// they appear in `cameras`). Overall option order matches enumeration order too,
// so the list is stable across reloads for the same device set.
export function build_camera_options(cameras: CameraDescriptor[]): CameraOption[] {
  const counts: Record<CameraFacing, number> = { back: 0, front: 0, unknown: 0 };
  return cameras.map((c) => {
    const facing: CameraFacing =
      c.facing === 'back' || c.facing === 'front' ? c.facing : 'unknown';
    counts[facing] += 1;
    return {
      device_id: c.device_id,
      friendly: `${FACING_PREFIX[facing]} ${counts[facing]}`,
      facing,
      label: c.label ?? '',
      detail: describe_settings(c),
      id_suffix: format_device_id_suffix(c.device_id),
    };
  });
}

// Look up the friendly name for a specific deviceId (used by CaptureService to
// log a manual override with the same name the UI shows). Falls back to a
// stable placeholder if the id is not among the options.
export function friendly_name_for(
  cameras: CameraDescriptor[],
  device_id: string
): string {
  const opt = build_camera_options(cameras).find((o) => o.device_id === device_id);
  return opt ? opt.friendly : '(unknown camera)';
}

// Seam for a FUTURE exact-device default rule. On a known device model we may
// eventually want to pre-select a specific lens (for example the correct
// ultra-wide camera on a particular phone) instead of the natural automatic
// pick. This is intentionally a no-op today: we have NOT yet confirmed which
// enumerated camera index is correct for the HUAWEI Mate XTs Ultimate Design (or
// any other model), so NOTHING is hardcoded. When the correct mapping is known,
// implement the model→option decision here; callers already treat null as "no
// device-specific default, use the existing automatic selection".
export function exact_device_default(
  _model: string | null,
  _options: CameraOption[]
): CameraOption | null {
  return null;
}
